import { type FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TextInput, Button, SimpleGrid, MultiSelect } from '@mantine/core';
import { getFetcher } from '@/api/fetchers';
import { type UserDetails as UserDetailsType } from '@/schemas/API/GET/users/response';
import api from '@/api/api';
import useSWR, { mutate } from 'swr';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { FormModal } from '@/components/Modal/FormModal';
import { type Services } from '@/schemas/API/GET/service/response';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { useDisclosure } from '@mantine/hooks';
import {
  displayError,
  displayErrorOnReactHookForm,
} from '@/utils/error/errorHandler';
import { useForm } from 'react-hook-form';
import { goodNotification } from '@/components/Notification/Notification';

interface ChangeEmailRequest {
  email: string;
  confirmEmail: string;
}

export const UserDetailsPage = (): JSX.Element => {
  const { userId } = useParams<{ userId: string }>();
  const userDetailsKey = userId ? `users/${userId}` : null;
  const { data: userDetails, mutate: mutateUserDetails } =
    useSWR<UserDetailsType>(userDetailsKey, getFetcher);
  const [isBlocked, setIsBlocked] = useState(userDetails?.blocked);

  const { data: rolesMap } = useSWR<Mapping>('mappings/roles', getFetcher);
  const { data: availableServices } = useSWR<Services>('services', getFetcher);

  const [emailOpened, { toggle: toggleEmail }] = useDisclosure(false);
  const [statusOpened, { toggle: toggleStatus }] = useDisclosure(false);
  const [passwordOpened, { toggle: togglePassword }] = useDisclosure(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
    getValues,
  } = useForm<ChangeEmailRequest>();

  const changeEmail = handleSubmit(
    async (changeEmailRequest: ChangeEmailRequest) => {
      try {
        await api.put(`/users/${userId}`, {
          ...changeEmailRequest,
          blocked: userDetails?.blocked,
        });
        mutate(userDetailsKey);
        toggleEmail();
        reset();
        goodNotification('Email byl úspěšně obnoven');
      } catch (error: any) {
        displayErrorOnReactHookForm(error, setError);
      }
    }
  );

  useEffect(() => {
    if (userDetails) {
      setIsBlocked(userDetails.blocked);
    }
  }, [userDetails]);

  const resetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId) return;
    try {
      await api.post(`/users/${userId}/password-reset`);
      goodNotification(
        'Heslo bylo úspěšně obnoveno',
        'Na email byla odeslána zpráva'
      );
      togglePassword();
    } catch (err) {
      displayError(err);
    }
  };

  const changeStatus = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId) return;
    try {
      await api.put(`/users/${userId}`, {
        email: userDetails?.email,
        blocked: !userDetails?.blocked,
      });
      setIsBlocked(!isBlocked);
      await mutateUserDetails();
      toggleStatus();
      goodNotification(
        'Status byl úspěšně obnoven',
        'Uživatel je teď ' + isBlocked ? 'zablokován' : 'povolen'
      );
    } catch (err) {
      displayError(err);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SimpleGrid cols={2}>
        <div className="flex flex-col border rounded-card p-4 gap-4">
          <TextInput
            label="Jméno"
            value={userDetails?.info.firstName}
            readOnly
            size="md"
            radius="md"
            variant="filled"
            className="text-lg"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
          />
          <TextInput
            label="Příjmení"
            value={userDetails?.info.lastName}
            readOnly
            size="md"
            radius="md"
            variant="filled"
            className="text-lg"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
          />
          <TextInput
            label="Role"
            value={
              userDetails?.role && rolesMap
                ? rolesMap[userDetails.role]
                : undefined
            }
            readOnly
            size="md"
            radius="md"
            variant="filled"
            className="text-lg"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
          />
        </div>
        <div className="flex flex-col border rounded-card p-4 gap-4">
          <TextInput
            label="Email"
            value={userDetails?.email}
            readOnly
            size="md"
            radius="md"
            variant="filled"
            className="text-lg"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
          />
          <TextInput
            label="Status"
            value={isBlocked ? 'Zablokován' : 'Povolen'}
            readOnly
            size="md"
            radius="md"
            variant="filled"
            className="text-lg"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
          />

          <MultiSelect
            label="Služby"
            readOnly
            value={userDetails?.services
              .map(serviceId => {
                const service = availableServices?.find(
                  availableService => availableService.id === serviceId
                );
                return service?.name;
              })
              .filter(
                (serviceName): serviceName is string =>
                  serviceName !== undefined
              )}
            data={availableServices?.map(service => ({
              value: service.id.toString(),
              label: service.name,
            }))}
            variant="filled"
            size="md"
            radius="md"
            styles={{
              input: {
                borderColor: 'transparent',
              },
            }}
            comboboxProps={{ shadow: 'md' }}
          />
        </div>
      </SimpleGrid>

      <ButtonBar>
        <Button onClick={toggleEmail} size="md" radius="md" className="text-lg">
          Změnit email
        </Button>
        <Button
          onClick={togglePassword}
          size="md"
          radius="md"
          className="text-lg"
        >
          Přegenerovat heslo
        </Button>

        <Button
          onClick={toggleStatus}
          size="md"
          radius="md"
          className="text-lg"
          color={isBlocked ? 'green' : 'red'}
        >
          {isBlocked ? 'Odblokovat' : 'Zablokovat'}
        </Button>
      </ButtonBar>
      <FormModal
        opened={emailOpened}
        onClose={toggleEmail}
        onConfirm={changeEmail}
        title="Změna emailové adresy"
        confirmText="Změnit"
      >
        <TextInput
          label="Nový email"
          variant="filled"
          size="md"
          radius="md"
          className="text-lg"
          {...register('email', {
            validate: match => {
                const emailRegex =
                    /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
              if (!emailRegex.test(match)) return 'Zadejte platný email';
              if (match !== userDetails?.email) return true;
              return 'Nová emailová adresa se shoduje se starou emailovou adresou';
            },
          })}
          error={errors.email?.message}
        />
        <TextInput
          label="Zopakujte nový email"
          variant="filled"
          size="md"
          radius="md"
          className="text-lg"
          {...register('confirmEmail', {
            validate: match => {
              return (
                match === getValues('email') || 'Emailové adresy se neshodují'
              );
            },
          })}
          error={errors.confirmEmail?.message}
        />
      </FormModal>
      <FormModal
        opened={statusOpened}
        onClose={toggleStatus}
        onConfirm={changeStatus}
        title={isBlocked ? 'Odblokování uživatele' : 'Zablokování uživatele'}
        confirmText={isBlocked ? 'Odblokovat' : 'Zablokovat'}
        confirmButtonColor={isBlocked ? 'green' : 'red'}
      >
        <div className="text-lg">
          {isBlocked
            ? 'Skutečně si přejete odblokovat přístup uživateli?'
            : 'Skutečně si přejete zablokovat přístup uživateli?'}
        </div>
      </FormModal>
      <FormModal
        opened={passwordOpened}
        onClose={togglePassword}
        onConfirm={resetPassword}
        title="Přegenerování hesla"
        confirmText="Přegenerovat"
      >
        Skutečně si přejete přegenerovat heslo uživateli?
      </FormModal>
    </div>
  );
};
